import React, { useState, useEffect } from 'react';
import { getData } from '../firebase';
import { LEDGER_GROUPS, computeLedgerBalances } from '../ledgers';

const BRAND = '#0f6e56';

export default function TrialBalance({ user }) {
  const [data, setData] = useState(null);
  const [customLedgers, setCustomLedgers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const [invoices, expenses, pettycash, cashbook, bankbook, journal, ledgers] = await Promise.all([
        getData(user.uid, 'invoices'), getData(user.uid, 'expenses'),
        getData(user.uid, 'pettycash'), getData(user.uid, 'cashbook'),
        getData(user.uid, 'bankbook'), getData(user.uid, 'journal'),
        getData(user.uid, 'ledgers'),
      ]);
      setData({ invoices, expenses, pettycash, cashbook, bankbook, journal });
      setCustomLedgers(ledgers.map(l => ({ ...l, custom: true })));
      setLoading(false);
    };
    load();
  }, [user.uid]);

  const fmt = n => '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  if (loading) return <div style={{ textAlign: 'center', padding: 60, color: '#aaa' }}>Loading...</div>;

  const balances = computeLedgerBalances(data, customLedgers);
  const rows = Object.values(balances).filter(b => b.balance > 0.001);
  rows.sort((a, b) => {
    const order = ['ASSET', 'LIABILITY', 'EQUITY', 'INCOME', 'EXPENSE'];
    return order.indexOf(a.group) - order.indexOf(b.group);
  });

  const totalDebit = rows.filter(r => r.balanceSide === 'debit').reduce((s, r) => s + r.balance, 0);
  const totalCredit = rows.filter(r => r.balanceSide === 'credit').reduce((s, r) => s + r.balance, 0);
  const balanced = Math.abs(totalDebit - totalCredit) < 0.01;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700 }}>Trial Balance</h2>
        {balanced
          ? <span style={{ background: '#e1f5ee', color: BRAND, padding: '5px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600 }}>✓ Balanced</span>
          : <span style={{ background: '#fcebeb', color: '#a32d2d', padding: '5px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600 }}>⚠ Out of balance</span>
        }
      </div>
      <div style={{ fontSize: 13, color: '#888', marginBottom: 20 }}>As of {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} · Proves total debits equal total credits</div>

      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e8e8e8', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr style={{ background: '#f7f8fa' }}>
            <th style={{ padding: '12px 20px', textAlign: 'left', fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase' }}>Ledger Account</th>
            <th style={{ padding: '12px 20px', textAlign: 'left', fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase' }}>Type</th>
            <th style={{ padding: '12px 20px', textAlign: 'right', fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase' }}>Debit</th>
            <th style={{ padding: '12px 20px', textAlign: 'right', fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase' }}>Credit</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={4} style={{ textAlign: 'center', padding: 50, color: '#aaa' }}>No transactions recorded yet. Add invoices or expenses to see the trial balance.</td></tr>
            ) : rows.map((r, i) => {
              const g = LEDGER_GROUPS[r.group];
              return (
                <tr key={r.name} style={{ borderTop: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '11px 20px', fontSize: 14, fontWeight: 500 }}>{r.name}</td>
                  <td style={{ padding: '11px 20px' }}>
                    <span style={{ fontSize: 11, padding: '2px 10px', borderRadius: 10, background: g.bg, color: g.color, fontWeight: 600 }}>{g.label}</span>
                  </td>
                  <td style={{ padding: '11px 20px', textAlign: 'right', fontSize: 14, color: r.balanceSide === 'debit' ? '#0f6e56' : '#ccc', fontWeight: r.balanceSide === 'debit' ? 600 : 400 }}>{r.balanceSide === 'debit' ? fmt(r.balance) : '—'}</td>
                  <td style={{ padding: '11px 20px', textAlign: 'right', fontSize: 14, color: r.balanceSide === 'credit' ? '#a32d2d' : '#ccc', fontWeight: r.balanceSide === 'credit' ? 600 : 400 }}>{r.balanceSide === 'credit' ? fmt(r.balance) : '—'}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ background: balanced ? '#e1f5ee' : '#fcebeb', borderTop: '2px solid #e8e8e8' }}>
              <td colSpan={2} style={{ padding: '14px 20px', fontWeight: 700, fontSize: 15, color: balanced ? BRAND : '#a32d2d' }}>TOTAL</td>
              <td style={{ padding: '14px 20px', textAlign: 'right', fontWeight: 700, fontSize: 15, color: '#0f6e56' }}>{fmt(totalDebit)}</td>
              <td style={{ padding: '14px 20px', textAlign: 'right', fontWeight: 700, fontSize: 15, color: '#a32d2d' }}>{fmt(totalCredit)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      {!balanced && (
        <div style={{ marginTop: 16, padding: '12px 16px', background: '#fcebeb', borderRadius: 10, fontSize: 13, color: '#a32d2d', borderLeft: '3px solid #a32d2d' }}>
          Difference of {fmt(Math.abs(totalDebit - totalCredit))} — this usually means an opening balance or capital entry is needed. Add it via Voucher Entry.
        </div>
      )}
    </div>
  );
}
